import { Component, OnDestroy, OnInit } from "@angular/core";
import type { HubConnection } from '@microsoft/signalr';
import { HttpErrorResponse } from "@angular/common/http";
import { AccountingImportDuplicateType, AccountingImportJob, AccountingImportJobStatus, LibroFacturasEmitidas, LibroFacturasRecibidas } from "./accounting-import.model";
import { AccountingAccountsService } from "../accounting-accounts/accounting-accounts.service";
import { environment } from "../../../../environments/environment";
import { consts } from "../../../consts";
import * as FileSaver from 'file-saver';
import { ImportSumasSaldosComponent } from "./import-sumas-saldos.component";
import { MatDialog } from "@angular/material/dialog";
import { CompanyModeService } from "../../../services/company-mode.service";
import { AuthService } from "../../../core/guards/auth/auth.service";
import { NotificationService } from "../../../modules_v2/shared/services/notification.service";

const IMPORT_POLL_INTERVAL_MS = 5000;
// Último job lanzado: /importBook/active solo devuelve jobs en curso, así que si el usuario
// sale de la pantalla y el job termina mientras tanto, sin esto no vería el resultado al volver.
const LAST_JOB_STORAGE_KEY = 'cf-accounting-import-last-job';

@Component({
    selector: "convfact-accounting-import",
    templateUrl: "./accounting-import.component.html",
    styleUrls: ["./accounting-import.component.scss"],
    standalone: false
})

export class AccountingImportComponent implements OnInit, OnDestroy {

  constructor(
    public accountingService: AccountingAccountsService,
    public dialog: MatDialog,
    private companyMode: CompanyModeService,
    private authService: AuthService,
    private notification: NotificationService,
  ) { }

  // El <select> nativo de app-select no tiene placeholder aquí, así que el navegador
  // ya muestra "Facturas Emitidas" seleccionada por ser la primera opción; si el modelo
  // arranca en '' nunca coincide con lo que se ve y el usuario nunca dispara (change).
  selectedOption: string = 'emitidas';

  tipoFacturaOptions = [
    { label: 'Facturas Emitidas', value: 'emitidas' },
    { label: 'Facturas Recibidas', value: 'recibidas' },
  ];

  // Asociaciones/fundaciones: la plantilla de recibidas trae proyecto, financiador, partida, subpartida y familia.
  isNonprofit = false;

  done = false;
  loading = false;
  importDone = false;
  error = false;
  errorList: string[];
  preview = false;

  formData: FormData;
  fileSelected = false;

  importBookEmitidas: LibroFacturasEmitidas = new LibroFacturasEmitidas();
  importBookRecibidas: LibroFacturasRecibidas = new LibroFacturasRecibidas();

  dataSourceEmitidas: LibroFacturasEmitidas[];
  dataSourceRecibidas: LibroFacturasRecibidas[];

  emitidas = false;
  recibidas = false;

  progress: number = 0;
  hubConnection: HubConnection;
  private hubReady: Promise<void>;
  private destroyed = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  importJob: AccountingImportJob | null = null;
  importErrorMessage: string | null = null;
  readonly jobStatus = AccountingImportJobStatus;
  readonly duplicateType = AccountingImportDuplicateType;

  async ngOnInit(): Promise<void> {
    this.companyMode.isNonProfit().catch(() => false).then(nonprofit => { this.isNonprofit = nonprofit; });

    const signalR = await import('@microsoft/signalr');
    if (this.destroyed) { return; }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.apiUrl + consts.HUB_PROGRESS, {
        accessTokenFactory: () => this.authService.authToken,
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.on('AccountingImportProgress', (job: AccountingImportJob) => this.applyJob(job));
    this.hubConnection.on('AccountingImportFinished', (job: AccountingImportJob) => this.applyJob(job));
    this.hubConnection.onreconnected(() => this.subscribeToJob());

    this.hubReady = this.hubConnection.start().catch(err => console.error('Error al conectar con SignalR', err));

    const active = await this.accountingService.getActiveImportBookJob().catch(() => null);
    if (this.destroyed) { return; }
    if (active) {
      this.trackJob(active);
      return;
    }

    // Terminó mientras el usuario estaba en otra pantalla: enseñar el resultado una vez.
    const lastUid = this.readLastJobUid();
    if (lastUid) {
      const last = await this.accountingService.getImportBookJob(lastUid).catch(() => null);
      if (this.destroyed) { return; }
      this.clearLastJobUid();
      if (last?.finished) {
        this.trackJob(last);
        this.notifyFinished(last);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopPolling();
    if (this.hubConnection) {
      this.hubConnection.stop().catch(() => undefined);
    }
  }

  get importInProgress(): boolean {
    return !!this.importJob && !this.importJob.finished;
  }

  get previewRows(): (LibroFacturasEmitidas | LibroFacturasRecibidas)[] {
    return (this.emitidas ? this.dataSourceEmitidas : this.dataSourceRecibidas) ?? [];
  }

  get selectedCount(): number {
    return this.previewRows.filter(r => r.importar).length;
  }

  get sureDuplicates(): number {
    return this.previewRows.filter(r => r.duplicateType === AccountingImportDuplicateType.Sure).length;
  }

  get possibleDuplicates(): number {
    return this.previewRows.filter(r => r.duplicateType === AccountingImportDuplicateType.Possible).length;
  }

  importSumasSaldos() {
    this.dialog.open(ImportSumasSaldosComponent, {
      //width: '60%',
      //height: '70%',
    });

  }

  downloadExcel() {
    this.accountingService.downloadExcel().then(response => {
      FileSaver.saveAs(response, "Plantilla_saldos.xlsx");
    })
  }

  //Guardamos el fichero a importar
  fileChange(event) {
    const fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      const file: File = fileList[0];
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);
      this.formData = formData;
      this.fileSelected = true;
    }
  }

  onImportClick(): void {
    if (this.preview) { return; }
    if (!this.importInProgress) {
      this.progress = 0;
      this.importJob = null;
      this.importDone = false;
    }
    this.importErrorMessage = null;
    this.error = false;
    this.done = false;

    const emitidas = this.selectedOption === 'emitidas';
    this.emitidas = emitidas;
    this.recibidas = !emitidas;
    this.preview = true;

    this.accountingService.previewBook(this.formData, emitidas).then(response => {
      if (response.error) {
        this.error = true;
        this.errorList = Array.isArray(response.data) ? response.data.map(item => item.toString()) : [];
        return;
      }

      const rows = (response.data ?? []) as (LibroFacturasEmitidas | LibroFacturasRecibidas)[];
      rows.forEach(r => r.importar = r.duplicateType !== AccountingImportDuplicateType.Sure);

      if (emitidas) {
        this.dataSourceEmitidas = rows as LibroFacturasEmitidas[];
      } else {
        this.dataSourceRecibidas = rows as LibroFacturasRecibidas[];
      }
      this.done = true;
    }).catch(() => {
      this.error = true;
      this.errorList = [this.formData ? 'No se ha podido leer el Excel. Revisa el fichero e inténtalo de nuevo.' : 'Selecciona un fichero Excel.'];
    }).finally(() => {
      this.preview = false;
    });
  }


  async start(): Promise<void> {
    if (this.importInProgress) { return; }

    if (this.selectedCount === 0) {
      this.importErrorMessage = 'No hay ninguna factura marcada para importar.';
      return;
    }

    this.importDone = false;
    this.importErrorMessage = null;
    this.loading = true;
    this.progress = 0.5;

    try {
      const job = this.emitidas
        ? await this.accountingService.importBookEmitidas(this.dataSourceEmitidas.filter(r => r.importar))
        : await this.accountingService.importBookRecibidas(this.dataSourceRecibidas.filter(r => r.importar));
      this.trackJob(job);
    } catch (err) {
      this.loading = false;
      this.progress = 0;
      this.importErrorMessage = err instanceof HttpErrorResponse && err.status === 409 && err.error?.message
        ? err.error.message
        : 'No se ha podido iniciar la importación. Inténtalo de nuevo.';
    }
  }

  private trackJob(job: AccountingImportJob): void {
    this.importJob = null;
    this.applyJob(job, true);
    if (job.finished) { return; }

    this.saveLastJobUid(job.uid);
    this.loading = true;
    this.subscribeToJob();
    this.startPolling();
  }

  private async subscribeToJob(): Promise<void> {
    if (!this.importJob || this.importJob.finished || !this.hubConnection) { return; }
    await this.hubReady;
    await this.hubConnection.invoke('SubscribeAccountingImport', this.importJob.uid).catch(() => undefined);
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollTimer = setInterval(async () => {
      if (!this.importJob) { return; }
      const job = await this.accountingService.getImportBookJob(this.importJob.uid).catch(() => null);
      this.applyJob(job);
    }, IMPORT_POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private applyJob(job: AccountingImportJob, force = false): void {
    if (!job) { return; }
    if (!force && (!this.importJob || this.importJob.uid !== job.uid)) { return; }
    if (!force && this.importJob.finished) { return; }
    if (!force && !job.finished && job.processed < this.importJob.processed) { return; }

    const wasRunning = !!this.importJob && !this.importJob.finished;
    this.importJob = job;
    this.progress = Math.max(job.percentage, 0.5);

    if (job.finished) {
      this.stopPolling();
      this.loading = false;
      this.done = false;
      this.importDone = job.status !== AccountingImportJobStatus.Failed;
      this.hubConnection?.invoke('UnsubscribeAccountingImport', job.uid).catch(() => undefined);

      // Solo en la transición en curso → terminado (no al pintar un job ya terminado).
      if (wasRunning) {
        this.clearLastJobUid();
        this.notifyFinished(job);
      }
    }
  }

  // El resultado se pinta debajo de la tabla de previsualización, que con un libro largo
  // queda muy por debajo de la vista: sin toast ni scroll parecía que no terminaba nunca.
  private notifyFinished(job: AccountingImportJob): void {
    if (job.status === AccountingImportJobStatus.Completed) {
      this.notification.ok(`Importación finalizada: ${job.imported} facturas importadas.`);
    } else if (job.status === AccountingImportJobStatus.CompletedWithErrors) {
      this.notification.warning(`Importación finalizada: ${job.imported} importadas y ${job.failed} con errores.`);
    } else {
      this.notification.error('La importación no ha podido completarse.');
    }

    setTimeout(() => document.getElementById('ai-import-result')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  private readLastJobUid(): string | null {
    try { return localStorage.getItem(LAST_JOB_STORAGE_KEY); } catch { return null; }
  }

  private saveLastJobUid(uid: string): void {
    try { localStorage.setItem(LAST_JOB_STORAGE_KEY, uid); } catch { }
  }

  private clearLastJobUid(): void {
    try { localStorage.removeItem(LAST_JOB_STORAGE_KEY); } catch { }
  }

  public trackByIndex(i: number) { return i; }

}
